import { isLinkedClientContract } from '~/lib/linkedClientContract';
import { computed, ref, unref, type ComputedRef, type Ref } from 'vue';
import { toast } from 'vue-sonner';
import { deliverContractReady, ensureContractPublicAccess } from '~/lib/contractReadyDelivery';
import { buildProjectContractDraft, publishProjectContract } from '~/lib/projectContract';
import { contractDeliveryNeedsEmail, contractDeliverySuccessMessage, type ContractDeliveryMethod } from '~/lib/contractDeliveryOptions';
import { resolveDocumentRecipient, resolvePrimaryClientContact } from '~/lib/documentRecipients';
import { resolveWorkspaceOrigin } from '~/lib/tenantLinks';
import { toMillis } from '~/lib/timestamps';
import type { Client, Project, ProjectContract, UploadedFile, ContractFieldDefinition, ContractTableDefinition, ContractTableValues } from '~/types/models';

type MaybeRef<T> = Ref<T> | ComputedRef<T>;

interface Options {
    client: MaybeRef<Client | null>;
    canMutate: MaybeRef<boolean>;
    scopedFiles: MaybeRef<UploadedFile[]>;
    filesStore: any;
    clientStore: any;
    tenantStore: any;
    runtimeConfig: any;
    requestUrl: { origin: string };
    relatedProjects: MaybeRef<Project[]>;
    projectContractHref: (project: Project) => string | null;
    refreshResolvedClient: () => Promise<void>;
}

export function useClientWorkspaceContract(options: Options) {
    const isClientContractDialogOpen = ref(false);
    const clientContractTitle = ref('');
    const clientContractIntroMessage = ref('');
    const clientContractBodyText = ref('');
    const clientContractDocumentHtml = ref('');
    const clientContractFields = ref<ContractFieldDefinition[]>([]);
    const clientContractTables = ref<ContractTableDefinition[]>([]);
    const clientContractValues = ref<Record<string, string>>({});
    const clientContractProposalReferences = ref<Record<string, string>>({});
    const clientContractProposalReferenceLinks = ref<Record<string, string>>({});
    const clientContractTableValues = ref<ContractTableValues>({});
    const clientContractTemplateId = ref('');
    const clientContractTenantSignatureName = ref('');
    const clientContractContentMode = ref<'text' | 'file'>('text');
    const linkedClientContractFileId = ref('');
    const selectedClientContractUpload = ref<File | null>(null);
    const clientContractDeliveryMethod = ref<ContractDeliveryMethod>('email');
    const deliveryRecipient = computed(() => resolvePrimaryClientContact(unref(options.client)));
    const clientContractSaving = ref(false);
    const clientContractSending = ref(false);
    const clientContractUploading = ref(false);
    const clientContractPdfDownloading = ref(false);

    const clientContract = computed<ProjectContract | null>(() => unref(options.client)?.contract || null);
    const clientContractFiles = computed<UploadedFile[]>(() => {
        const clientId = unref(options.client)?.id;
        if (!clientId) return [];
        const filesByEntity = options.filesStore.filesByEntity?.[`client:${clientId}`];
        return filesByEntity || unref(options.scopedFiles).filter((file) => file.entityType === 'client' && file.entityId === clientId);
    });
    const linkedClientContractFile = computed<UploadedFile | null>(() => (
        clientContractFiles.value.find((file) => file.id === linkedClientContractFileId.value) || null
    ));
    const hasSelectedClientContractContent = computed(() => clientContractContentMode.value === 'text'
        ? Boolean(clientContractDocumentHtml.value.trim() || clientContractBodyText.value.trim())
        : Boolean(linkedClientContractFile.value?.fileURL));
    function contractStatusMeta(contract: ProjectContract | null | undefined): { label: string; variant: 'outline' | 'secondary' } {
        if (!contract) return { label: 'No contract', variant: 'outline' };
        if (contract.status === 'signed') return { label: 'Signed', variant: 'secondary' };
        if (contract.status === 'awaiting_client') return { label: 'Awaiting client', variant: 'outline' };
        return { label: 'Draft', variant: 'outline' };
    }
    const clientContracts = computed(() => {
        const client = unref(options.client);
        const linkedProjects = client ? unref(options.relatedProjects).filter(project => isLinkedClientContract(project, client.id, clientContract.value)) : [];
        const items: Array<{ id: string; scopeLabel: string; title: string; status: string; badgeVariant: 'outline' | 'secondary'; summary: string; href: string | null; updatedAt: unknown }> = [];
        if (client && clientContract.value) {
            const meta = contractStatusMeta(clientContract.value);
            items.push({
                id: `client:${client.id}:contract`, scopeLabel: linkedProjects.length ? `Client workspace · Linked to ${linkedProjects.map(project => project.name || 'Project').join(', ')}` : 'Client workspace',
                title: clientContract.value.title || client.name || 'Client contract', status: meta.label, badgeVariant: meta.variant,
                summary: clientContract.value.introMessage || clientContract.value.bodyText || 'Client-level agreement ready from the delivery workspace.',
                href: null, updatedAt: clientContract.value.updatedAt,
            });
        }
        for (const project of unref(options.relatedProjects)) {
            if (!project.contract) continue;
            if (linkedProjects.some(linked => linked.id === project.id)) continue;
            const meta = contractStatusMeta(project.contract);
            items.push({
                id: `project:${project.id}:contract`, scopeLabel: 'Project',
                title: project.contract.title || project.name || 'Project contract', status: meta.label, badgeVariant: meta.variant,
                summary: project.contract.introMessage || project.contract.bodyText || project.description || 'Project-level agreement ready for this delivery stream.',
                href: options.projectContractHref(project), updatedAt: project.contract.updatedAt,
            });
        }
        return items.sort((left, right) => toMillis(right.updatedAt) - toMillis(left.updatedAt));
    });

    function syncClientContractDraft() {
        const contract = clientContract.value;
        clientContractTitle.value = contract?.title || '';
        clientContractIntroMessage.value = contract?.introMessage || '';
        clientContractBodyText.value = contract?.bodyText || '';
        clientContractDocumentHtml.value = contract?.documentHtml || '';
        clientContractFields.value = contract?.fieldDefinitions || [];
        clientContractTables.value = contract?.tableDefinitions || [];
        clientContractValues.value = contract?.fieldValues || {};
        clientContractProposalReferences.value = contract?.proposalReferences || {};
        clientContractProposalReferenceLinks.value = contract?.proposalReferenceLinks || {};
        clientContractTableValues.value = contract?.tableValues || {};
        clientContractTemplateId.value = contract?.templateId || '';
        clientContractTenantSignatureName.value = '';
        linkedClientContractFileId.value = contract?.documentFileId || '';
        clientContractContentMode.value = contract?.sourceType === 'file' ? 'file' : 'text';
        selectedClientContractUpload.value = null;
    }

    function openClientContractDialog() {
        syncClientContractDraft();
        isClientContractDialogOpen.value = true;
    }

    function openCreateContractDialog() {
        openClientContractDialog();
    }

    async function saveClientContractDraft() {
        const client = unref(options.client);
        if (!client?.id) return;
        clientContractSaving.value = true;
        try {
            const nextContract = buildProjectContractDraft({
                existingContract: client.contract,
                scope: 'client',
                clientId: client.id,
                defaultTitle: 'Client agreement',
                title: clientContractTitle.value,
                introMessage: clientContractIntroMessage.value,
                bodyText: clientContractContentMode.value === 'text' ? clientContractBodyText.value : undefined,
                documentHtml: clientContractContentMode.value === 'text' ? clientContractDocumentHtml.value : undefined,
                fieldDefinitions: clientContractFields.value,
                fieldValues: clientContractValues.value,
                proposalReferences: clientContractProposalReferences.value,
                proposalReferenceLinks: clientContractProposalReferenceLinks.value,
                tableDefinitions: clientContractTables.value,
                tableValues: clientContractTableValues.value,
                templateId: clientContractTemplateId.value,
                documentFile: clientContractContentMode.value === 'file' && linkedClientContractFile.value
                    ? { id: linkedClientContractFile.value.id, fileName: linkedClientContractFile.value.fileName, fileURL: linkedClientContractFile.value.fileURL, contentType: linkedClientContractFile.value.contentType }
                    : null,
            });
            await options.clientStore.updateClient(client.id, { contract: nextContract });
            await options.refreshResolvedClient();
            toast.success(nextContract ? 'Client contract draft saved.' : 'Client contract cleared.');
        } catch (error: any) {
            toast.error(error?.message || 'Unable to save the client contract.');
        } finally {
            clientContractSaving.value = false;
        }
    }

    async function sendClientContract() {
        const client = unref(options.client);
        const tenantId = options.tenantStore.currentTenantId;
        if (!client?.id || !tenantId || !unref(options.canMutate) || clientContractSending.value) return;
        if (client.contract?.status === 'signed') return;
        if (client.contract?.status !== 'awaiting_client' && !clientContractTenantSignatureName.value.trim()) return void toast.error('Type your full name to sign this contract before sending.');
        clientContractSending.value = true;
        try {
            const resolvedDeliveryRecipient = await resolveDocumentRecipient({ clientId: client.id });
            if (contractDeliveryNeedsEmail(clientContractDeliveryMethod.value) && !resolvedDeliveryRecipient.recipientEmail) {
                throw new Error('Add an email address to the client contact before emailing this contract.');
            }
            const { $auth } = useNuxtApp();
            const authUser = $auth.currentUser;
            const nextContract = client.contract?.status === 'awaiting_client' ? client.contract : publishProjectContract({
                existingContract: client.contract,
                scope: 'client',
                clientId: client.id,
                defaultTitle: 'Client agreement',
                title: clientContractTitle.value,
                introMessage: clientContractIntroMessage.value,
                bodyText: clientContractContentMode.value === 'text' ? clientContractBodyText.value : undefined,
                documentHtml: clientContractContentMode.value === 'text' ? clientContractDocumentHtml.value : undefined,
                fieldDefinitions: clientContractFields.value,
                fieldValues: clientContractValues.value,
                proposalReferences: clientContractProposalReferences.value,
                proposalReferenceLinks: clientContractProposalReferenceLinks.value,
                tableDefinitions: clientContractTables.value,
                tableValues: clientContractTableValues.value,
                templateId: clientContractTemplateId.value,
                documentFile: clientContractContentMode.value === 'file' && linkedClientContractFile.value
                    ? { id: linkedClientContractFile.value.id, fileName: linkedClientContractFile.value.fileName, fileURL: linkedClientContractFile.value.fileURL, contentType: linkedClientContractFile.value.contentType }
                    : null,
                signerId: authUser?.uid,
                signerName: clientContractTenantSignatureName.value.trim(),
                signerEmail: authUser?.email || '',
                signatureText: clientContractTenantSignatureName.value.trim(),
                acceptanceConfirmed: true,
            });
            if (!nextContract) {
                toast.error('Add contract text or link a contract document before sending.');
                return;
            }
            const resolvedRecipient = resolvedDeliveryRecipient;
            const workspaceOrigin = resolveWorkspaceOrigin({ appDomain: String(options.runtimeConfig.public.appDomain || ''), fallbackOrigin: options.requestUrl.origin });
            const { contract: publicContract, publicLink } = ensureContractPublicAccess({ contract: nextContract, origin: workspaceOrigin, tenantId, clientId: client.id });
            await options.clientStore.updateClient(client.id, { contract: publicContract });
            await options.refreshResolvedClient();
            await deliverContractReady({
                tenantId,
                contract: publicContract,
                publicLink,
                deliveryMethod: clientContractDeliveryMethod.value,
                recipientName: resolvedRecipient.recipientName,
                recipientEmail: resolvedRecipient.recipientEmail,
                clientId: resolvedRecipient.clientId,
                clientSlug: client.slug || resolvedRecipient.client?.slug,
                clientName: client.name,
                actorId: authUser?.uid,
                actorName: authUser?.displayName || options.tenantStore.currentTenant?.name || 'Team',
            });
            toast.success(contractDeliverySuccessMessage(clientContractDeliveryMethod.value));
            isClientContractDialogOpen.value = false;
        } catch (error: any) {
            toast.error(error?.message || 'Unable to send the client contract.');
        } finally {
            clientContractSending.value = false;
        }
    }

    function onClientContractFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        selectedClientContractUpload.value = input.files?.[0] || null;
        if (selectedClientContractUpload.value) void handleUploadClientContractFile();
    }

    async function handleUploadClientContractFile() {
        const client = unref(options.client);
        if (!client?.id || !selectedClientContractUpload.value) return;
        clientContractUploading.value = true;
        try {
            const fileId = await options.filesStore.uploadFile('client', client.id, selectedClientContractUpload.value);
            await options.filesStore.fetchFiles('client', client.id);
            linkedClientContractFileId.value = fileId;
            selectedClientContractUpload.value = null;
            clientContractContentMode.value = 'file';
            toast.success('Contract file uploaded and linked.');
        } catch (error: any) {
            console.error('Failed to upload client contract file:', error);
            toast.error(error?.message || 'Failed to upload contract file.');
        } finally {
            clientContractUploading.value = false;
        }
    }

    function resetClientContractDraft() {
        clientContractTitle.value = '';
        clientContractIntroMessage.value = '';
        clientContractBodyText.value = '';
        clientContractDocumentHtml.value = '';
        clientContractFields.value = [];
        clientContractTables.value = [];
        clientContractValues.value = {};
        clientContractProposalReferences.value = {};
        clientContractProposalReferenceLinks.value = {};
        clientContractTableValues.value = {};
        clientContractTemplateId.value = '';
        clientContractTenantSignatureName.value = '';
        linkedClientContractFileId.value = '';
        selectedClientContractUpload.value = null;
        clientContractContentMode.value = 'text';
    }

    function formatContractSigner(signature?: { signerName?: string; signerEmail?: string } | null): string {
        if (!signature) return 'Not signed yet';
        return signature.signerName || signature.signerEmail || 'Signed';
    }

    async function downloadClientContractPdf() {
        const client = unref(options.client);
        const tenantId = options.tenantStore.currentTenantId;
        if (!client?.id || !tenantId) return;
        const { $auth } = useNuxtApp();
        const token = await $auth.currentUser?.getIdToken();
        if (!token) {
            toast.error('You need to be signed in to download the contract PDF.');
            return;
        }
        clientContractPdfDownloading.value = true;
        try {
            const response = await fetch(`/api/contracts/signed-pdf?tenantId=${encodeURIComponent(tenantId)}&clientId=${encodeURIComponent(client.id)}`, { headers: { authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error(await response.text() || 'Failed to generate contract PDF.');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = `${(client.companyName || client.name || 'client-contract').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}-signed-contract.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (error: any) {
            console.error('Failed to download client contract PDF:', error);
            toast.error(error?.message || 'Failed to download client contract PDF.');
        } finally {
            clientContractPdfDownloading.value = false;
        }
    }

    return {
        isClientContractDialogOpen,
        clientContractTitle,
        clientContractIntroMessage,
        clientContractBodyText,
        clientContractDocumentHtml,
        clientContractFields,
        clientContractTables,
        clientContractValues,
        clientContractProposalReferences,
        clientContractProposalReferenceLinks,
        clientContractTableValues,
        clientContractTemplateId,
        clientContractTenantSignatureName,
        clientContractContentMode,
        linkedClientContractFileId,
        selectedClientContractUpload,
        clientContractSaving,
        clientContractSending,
        clientContractDeliveryMethod,
        deliveryRecipient,
        clientContractUploading,
        clientContractPdfDownloading,
        clientContract,
        clientContractFiles,
        linkedClientContractFile,
        hasSelectedClientContractContent,
        clientContracts,
        contractStatusMeta,
        syncClientContractDraft,
        openClientContractDialog,
        openCreateContractDialog,
        saveClientContractDraft,
        sendClientContract,
        onClientContractFileSelected,
        handleUploadClientContractFile,
        resetClientContractDraft,
        formatContractSigner,
        downloadClientContractPdf,
    };
}
